const PBKDF2_ITERATIONS = 100000;

// BIP39-style word list for recovery key generation (simplified - 256 common words)
const WORD_LIST = [
  'abandon', 'ability', 'able', 'about', 'above', 'absent', 'absorb', 'abstract',
  'absurd', 'abuse', 'access', 'accident', 'account', 'accuse', 'achieve', 'acid',
  'acoustic', 'acquire', 'across', 'action', 'actor', 'actress', 'actual', 'adapt',
  'address', 'adjust', 'admit', 'adult', 'advance', 'advice', 'aerobic', 'affair',
  'afford', 'afraid', 'again', 'agent', 'agree', 'ahead', 'alarm', 'album',
  'alcohol', 'alert', 'alien', 'allow', 'almost', 'alone', 'alpha', 'already',
  'also', 'alter', 'always', 'amazing', 'among', 'amount', 'anchor', 'ancient',
  'anger', 'angle', 'animal', 'ankle', 'announce', 'annual', 'another', 'answer',
  'antenna', 'antique', 'anxiety', 'any', 'apart', 'apology', 'appear', 'apple',
  'approve', 'april', 'arch', 'arctic', 'arena', 'argue', 'armor', 'army',
  'around', 'arrange', 'arrest', 'arrive', 'arrow', 'artist', 'artwork', 'aspect',
  'assault', 'asset', 'assist', 'assume', 'asthma', 'athlete', 'atom', 'attack',
  'attend', 'attract', 'auction', 'audit', 'august', 'aunt', 'author', 'autumn',
  'average', 'avoid', 'awake', 'aware', 'away', 'awesome', 'awful', 'awkward',
  'bacon', 'badge', 'balance', 'balcony', 'ball', 'bamboo', 'banana', 'banner',
  'barrel', 'base', 'basic', 'basket', 'battle', 'beach', 'bean', 'beauty',
  'become', 'bedroom', 'before', 'begin', 'behave', 'behind', 'believe', 'below',
  'belt', 'bench', 'benefit', 'best', 'betray', 'better', 'between', 'beyond',
  'bicycle', 'bird', 'birth', 'bitter', 'black', 'blade', 'blame', 'blanket',
  'blast', 'bleak', 'bless', 'blind', 'blood', 'blossom', 'blouse', 'blue',
  'blur', 'board', 'boat', 'body', 'boil', 'bomb', 'bone', 'bonus',
  'book', 'boost', 'border', 'boring', 'borrow', 'boss', 'bottom', 'bounce',
  'brain', 'brand', 'brass', 'brave', 'bread', 'breeze', 'brick', 'bridge',
  'brief', 'bright', 'bring', 'brisk', 'broccoli', 'broken', 'bronze', 'broom',
  'brother', 'brown', 'brush', 'bubble', 'buddy', 'budget', 'buffalo', 'build',
  'bulb', 'bulk', 'bullet', 'bundle', 'bunker', 'burden', 'burger', 'burst',
  'butter', 'buyer', 'cabin', 'cable', 'cactus', 'cage', 'cake', 'call',
  'calm', 'camera', 'camp', 'canal', 'cancel', 'candy', 'cannon', 'canoe',
  'canvas', 'canyon', 'capable', 'capital', 'captain', 'carbon', 'card', 'cargo',
  'carpet', 'carry', 'cart', 'case', 'castle', 'casual', 'catch', 'category',
  'cattle', 'caught', 'cause', 'caution', 'cave', 'ceiling', 'celery', 'cement',
  'census', 'century', 'cereal', 'certain', 'chair', 'chalk', 'champion', 'change'
];

function bufferToBase64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function base64ToBuffer(base64) {
  const binary = atob(base64);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

export function generateSalt() {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return bufferToBase64(salt);
}

export async function deriveKey(password, saltBase64) {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: base64ToBuffer(saltBase64),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function encryptContent(content, key) {
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(content)
  );

  return {
    content: bufferToBase64(encrypted),
    iv: bufferToBase64(iv),
  };
}

export async function decryptContent(encryptedContent, ivBase64, key) {
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBuffer(ivBase64) },
    key,
    base64ToBuffer(encryptedContent)
  );

  return new TextDecoder().decode(decrypted);
}

export async function exportKeyToJwk(key) {
  return crypto.subtle.exportKey('jwk', key);
}

export async function importKeyFromJwk(jwk) {
  return crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'AES-GCM' },
    true,
    ['encrypt', 'decrypt']
  );
}

// ==================== Recovery Key Functions ====================

/**
 * Generate a 24-word recovery phrase for backup encryption
 */
export function generateRecoveryPhrase() {
  const words = [];
  const randomBytes = crypto.getRandomValues(new Uint8Array(32)); // 256 bits of entropy
  
  for (let i = 0; i < 24; i++) {
    // Use 8 bits (one byte) + some overlap for each word selection
    const index = randomBytes[i % 32] ^ (i < 8 ? 0 : randomBytes[(i + 8) % 32]);
    words.push(WORD_LIST[index % WORD_LIST.length]);
  }
  
  return words.join(' ');
}

/**
 * Hash the recovery phrase to store a verifiable fingerprint (not the actual key)
 */
export async function hashRecoveryPhrase(phrase) {
  const encoder = new TextEncoder();
  const data = encoder.encode(phrase);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return bufferToBase64(hashBuffer);
}

/**
 * Derive an encryption key from the recovery phrase
 */
export async function deriveKeyFromRecoveryPhrase(phrase) {
  // Use a fixed salt for recovery phrase key derivation
  const fixedSalt = 'VaultNotes-Recovery-Key-Salt-v1';
  const encoder = new TextEncoder();
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(phrase),
    'PBKDF2',
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(fixedSalt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt backup data with recovery key
 */
export async function encryptBackup(data, recoveryPhrase) {
  const key = await deriveKeyFromRecoveryPhrase(recoveryPhrase);
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(JSON.stringify(data))
  );

  return {
    version: 1,
    encrypted: bufferToBase64(encrypted),
    iv: bufferToBase64(iv),
    created_at: new Date().toISOString()
  };
}

/**
 * Decrypt backup data with recovery key
 */
export async function decryptBackup(encryptedBackup, recoveryPhrase) {
  const key = await deriveKeyFromRecoveryPhrase(recoveryPhrase);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBuffer(encryptedBackup.iv) },
    key,
    base64ToBuffer(encryptedBackup.encrypted)
  );

  return JSON.parse(new TextDecoder().decode(decrypted));
}
